import axios from 'axios';
import {
  LegislationInfos,
  LegislationDecision,
  LinkedArticleType,
  LegislationAllInfosType,
  LegislationBreadcrumbList,
  ParliamentaryQuestionsType,
  ArticleStatsResponseType,
} from './legislation.types';
import { Commentary } from '../../commentary/commentary.types';
import { SearchAPIResponse, SearchParams } from '../../search/search.types';

import { getAggregations } from '../../search/state/search.api';

import {
  getParliamentaryQuestionsAggregation,
  getBOFIPAggregation,
} from './article/page/components/search-query-section/utils/utils';

export const getLegislationInfos = async ({
  articleId,
  query = '',
}: {
  articleId: string;
  query?: string;
}): Promise<LegislationInfos> => {
  return (
    await axios.get(`/api/legislation/article/${articleId}`, {
      params: { q: query },
    })
  ).data;
};

/**
 *
 * @param textId the identifier of the parent text (a codified text)
 * @param articleId the identifier of the article for which we want to retrieve breadcrumbs
 */
export async function getLegislationBreadcrumb(
  textId: string,
  articleId: string,
): Promise<LegislationBreadcrumbList> {
  return (
    await axios.get(`/api/legislation/articles/${articleId}/breadcrumb`, {
      params: { parent_text_id: textId },
    })
  ).data;
}

export async function getLegislationCodeBreadcrumb(
  textId: string,
  codeId: string,
  date: Date | string,
): Promise<LegislationBreadcrumbList> {
  return (
    await axios.get(`/api/legislation/sections/${codeId}/breadcrumb`, {
      params: { date, parent_text_id: textId },
    })
  ).data;
}

export async function getAllVersionsLegislation(
  articleId: string,
): Promise<LegislationAllInfosType> {
  return (
    await axios.get('/getAllVersionsLaw', {
      params: {
        articleId,
      },
    })
  ).data;
}

export async function getLegislationQpcDecisions(
  articleId: string,
): Promise<LegislationDecision[]> {
  return (
    await axios.get('/getLawQPCDecisions', {
      params: {
        articleId,
      },
    })
  ).data.qpcDecisions;
}

export async function getLegislationReferenceDecisions(
  articleId: string,
): Promise<LegislationDecision[]> {
  return (
    await axios.get('/getLawReferenceDecisions', {
      params: {
        articleId,
      },
    })
  ).data.refdecisions;
}

export async function getLinkedLegislationArticles(
  articleId: string,
  category: string | null = null,
  size: number | null = null,
  content: boolean | null = null,
): Promise<LinkedArticleType> {
  return (
    await axios.get(`/api/legislation/articles/${articleId}/linked`, {
      params: {
        category,
        size,
        content,
      },
    })
  ).data;
}

export async function getLegislationCommentaries(
  articleId: string,
  limit?: number,
  offset?: number,
): Promise<{
  commentaries: Array<Commentary>;
  hasMore: boolean;
  commentariesFullCount: string;
}> {
  return (
    await axios.get(`/api/legislation/articles/${articleId}/commentaries`, {
      params: {
        limit,
        offset,
      },
    })
  ).data;
}

export async function getArticleStats(
  articleId: string,
): Promise<ArticleStatsResponseType> {
  return (await axios.get(`/api/legislation/articles/${articleId}/stats`)).data;
}

export async function getParliamentaryQuestions(
  articleId: string,
  limit?: number,
  offset?: number,
): Promise<ParliamentaryQuestionsType> {
  return (
    await axios.get(
      `/api/legislation/articles/${articleId}/parliamentary-questions`,
      {
        params: {
          limit,
          offset,
        },
      },
    )
  ).data;
}

export async function searchWithinRestrictedArticles(
  params: SearchParams,
  loggedIn: boolean = true,
): Promise<SearchAPIResponse> {
  const endpoint = loggedIn ? '/api/search' : '/api/search/sample';

  return (
    await axios.get(endpoint, {
      params,
    })
  ).data;
}

export const getFiltersCount = async (
  params: SearchParams,
  articleId: string,
  loggedIn: boolean,
): Promise<{ pq: { count: number }; bofip: { count: number } }> => {
  // If we are loggedIn, we took count from the search in order to benefit its features.
  if (loggedIn) {
    const { data } = await getAggregations(params);
    const fromSearchCount = getParliamentaryQuestionsAggregation(data);
    const fromSearchBofipCount = getBOFIPAggregation(data);
    return { pq: fromSearchCount, bofip: fromSearchBofipCount };
  }
  // If not loggedIn, we took count from DB in order to not be restricted by the search limitation.
  const { questionsFullCount: fromDBCount } = await getParliamentaryQuestions(
    articleId,
    1,
  );
  return { pq: { count: Number(fromDBCount) }, bofip: { count: 0 } };
};
